import { useEffect, useState } from 'react';

/**
 * The one line between the two layouts.
 *
 * Below it the product stops being a desktop application and becomes a phone
 * one: no rails in flow, one column, the table becomes cards, Robbie moves
 * behind a button. Above it nothing changes at all — every mobile rule in the
 * app is written as a base class that a `md:` class puts back, so the desktop
 * layout the product shipped with is the `md:` layout to the pixel.
 *
 * 768px is Tailwind's own `md` boundary on purpose. Components that can switch
 * with CSS alone use `md:`; the ones that have to switch their markup (a table
 * that becomes cards, a rail that becomes a drawer) use this hook, and the two
 * can never drift apart because they are the same number.
 */
export const MOBILE_MAX_WIDTH = 767;

const QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

/** Server/JSDOM and any browser without matchMedia get the desktop layout. */
const readMatches = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(QUERY).matches;
};

/**
 * Read synchronously on the first render, so a phone never paints the desktop
 * layout for a frame before correcting itself.
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(readMatches);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    // Rotating a phone, or resizing a desktop window across the line.
    media.addEventListener('change', onChange);
    // The query can already have flipped between first render and this effect.
    setIsMobile(media.matches);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return isMobile;
};

export default useIsMobile;
